'use client';

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from '../utils/cn';

import type { SzczegolyBledu } from './EkranBledu';
import { SygnetZywy } from './SygnetZywy';
import { bezSierotekWTresci } from './typografia';

/**
 * Ekran startowy — czekanie na system (sesja, uprawnienia, pierwszy widok).
 *
 * `pelny`: znak pionowy (`SKALA_ZNAKU.pionowe`, 96 px) stoi od pierwszej
 * klatki — ten sam obraz i rozmiar, co w
 * `index.html` powłoki przed pierwszym bajtem JavaScriptu, więc start jest
 * jednym ciągłym obrazem, a nie trzema różnymi spinnerami. Po
 * `--ids-dur-slow` sieć zaczyna pracować (impuls z rdzenia do organów),
 * a po 8 s pojawia się wiersz „To trwa dłużej niż zwykle.” — oba czasy
 * w CSS.
 *
 * WIERSZ „DŁUŻEJ” TO JEDNO ZDANIE W JEDNYM MIEJSCU (runda 7c). Ekran bez
 * własnych akcji mówi DOKŁADNIE to, co wstawka `index.html` powłoki:
 * „To trwa dłużej niż zwykle. Odśwież stronę” (odnośnik przeładowuje
 * stronę — przed hydratacją i po niej tak samo), tym samym krojem i w tym
 * samym miejscu (12 px pod znakiem, bez nazwy). Do rundy 7c komponent mówił
 * „To trwa dłużej niż zwykle…”, a wstawka — swoje, krojem systemowym, więc
 * hydratacja po 8 s podmieniała treść wiersza i jego szerokość. Ekran
 * z własnymi akcjami (`akcje`, np. aktywacja UCS) ma lepsze wyjścia niż
 * przeładowanie i mówi samo „To trwa dłużej niż zwykle.” — dwie drogi
 * ponowienia obok siebie byłyby wyborem bez różnicy. Wiersz jest widoczny
 * przez `visibility` (nie samo krycie): niewidoczny odnośnik nie łapie
 * fokusu, a niewidoczne zdanie nie trafia do czytnika. Wiersz liczy 8 s od
 * pierwszego obrazu startu — także od wstawki (`[data-ids-wstawka-startu]`),
 * której animacja gra od pierwszego malowania: React zamontowany po 8 s nie
 * chowa wiersza, który już stoi. Praca sieci zaczyna się przy tym od
 * spoczynku jak dotąd, bo znak wstawki stoi nieruchomo.
 * Bramki: `tests/unit/apps/startPowlokiJednymObrazem.test.tsx` (treść
 * i pudełko wiersza ±0,5 px) i `tests/unit/marka/ciagloscStartu.test.tsx`
 * (montaż po wstawce).
 *
 * Nazwa pod znakiem ma typografię tekstu pod znakiem pionowym
 * (`.ids-znak-podpis`: 11 px, wersaliki, 16 px pod napisem — pole ochronne
 * pionowego 96 to 14,39 px, zaokrąglone w górę do siatki 4 px).
 *
 * `w-tresci`: przez `--ids-dur-slow` puste miejsce, potem mały znak
 * (`SKALA_ZNAKU.startWTresci`) w ruchu. Szybkie ładowanie nie mignie niczym.
 *
 * TREŚĆ I AKCJE POD ZNAKIEM (`tresc`, `akcje` — runda 7b). Start, który
 * trwa dłużej i ma coś do powiedzenia (aktywacja konta organu: co się
 * dzieje, wyjście na pulpit, po limicie ponowienie), dostaje miejsce pod
 * znakiem W PRZEPŁYWIE: nazwa, wiersz „To trwa dłużej…”, 24 px niżej treść
 * (`.ids-tresc-ekranu`, typografia `bezSierotekWTresci`), 32 px niżej akcje
 * (ten sam wiersz co `EkranBledu`: pierwsza `btn btn--primary btn--lg`,
 * drugorzędna `btn btn--secondary btn--lg`, kolumna na telefonie). Znak
 * zostaje w punkcie startu — górny odstęp to pół ekranu minus pół znaku,
 * zamiast centrowania kolumny — więc treść nie przesuwa obrazu, a niskie
 * okno (telefon poziomo 844 × 390) przewija stronę zamiast wypychać treść
 * poza zasięg. Do rundy 7b aplikacja wieszała treść absolutnie od 50 %
 * wysokości i powtarzała liczby arkusza (11 px × 1,4, 14 px × 1,5).
 *
 * AWARIA W TRAKCIE STARTU (`trwa={false}`, `szczegoly` — runda 7c). Gdy
 * ekran startu już stoi, a czekanie przerywa awaria (aktywacja UCS dostaje
 * 5xx w połowie odpytywania), ekran ZOSTAJE: ten sam węzeł znaku (aplikacja
 * podaje `tryb="oddech"` — system czeka na człowieka), w miejscu treści
 * zdanie awarii, w akcjach ponowienie i wyjście, pod nimi szczegóły
 * techniczne (kod, ścieżka — ten sam element, co w `EkranBledu`). Bez
 * wiersza „dłużej”: nic już nie trwa, więc byłby nieprawdą. Przejście na
 * `EkranBledu` przenosiło znak z punktu startu na kotwicę ekranu błędu
 * (Δ 154 px na desktopie) i zamieniało znak pionowy w sam sygnet.
 *
 * Region `role="status"`: bez treści cały ekran (czytnik słyszy komunikat,
 * a po 8 s wiersz „dłużej” z odnośnikiem — nazwa jest `aria-hidden`);
 * z treścią SAM komunikat. Treść i akcje są wtedy zwykłą zawartością strony, nie regionem
 * na żywo: status ma niejawne `aria-atomic`, więc każda zmiana (np. zdanie po
 * limicie prób) czytałaby od nowa cały ekran razem z etykietami przycisków.
 *
 * TRYB RUCHU (`tryb`): `praca` — system pracuje (domyślnie); `oddech` —
 * system czeka na człowieka (§1 docs/marka/ZNAK-W-SYSTEMIE.md), np. po
 * limicie prób, gdy nic już samo nie ponawia. Zmiana trybu to ten sam węzeł
 * znaku i ten sam ekran — start nie zaczyna się od nowa.
 *
 * CIĄGŁOŚĆ MIĘDZY MONTAŻAMI. Start organu to dwa kolejne ekrany w dwóch
 * miejscach drzewa: AuthGuard (czeka na sesję), potem bramka organu (czeka na
 * `ma_dostep`) — więc dwie instancje. Bez pamięci druga zaczynała od zera:
 * nazwa organu miała znowu krycie 0 przez `--ids-dur-slow` (napis „AGENDA"
 * znikał i wracał), a praca sieci stawała i ruszała od nowa. Pamięć startu
 * jest więc POZA instancją (zmienna modułu, osobno dla każdego układu):
 * montaż, przy którym inny ekran tego układu stoi albo zniknął najwyżej
 * `OKNO_CIAGLOSCI_MS` temu, jest dalszym ciągiem tamtego startu i podaje
 * w `--ids-start-uplynelo`, ile start już trwa — CSS odejmuje to od każdego
 * opóźnienia. Pierwszy start (i każdy po dłuższej przerwie) zachowuje
 * opóźnienia bez zmian. Bramki: `tests/unit/marka/ciagloscStartu.test.tsx`
 * (logika okna i skutek w Chromium).
 *
 * START Z SERWERA. W organach (Next) pierwszy ekran przychodzi w HTML-u
 * i jego animacje grają od PIERWSZEGO MALOWANIA, a React widzi go dopiero
 * przy hydratacji — przy wolnym łączu sekundę później. Początek nowego startu
 * bierze się więc z animacji, które już grają w poddrzewie
 * (`poczatekAnimacji`), a chwila montażu jest tylko zapasem: inaczej bramka
 * zamontowana chwilę po hydratacji liczyła `uplynelo` od hydratacji i nazwa
 * wracała do krycia 0.
 *
 * Nigdy nie ma minimalnego czasu wyświetlania ani animacji wyjścia: znika
 * w chwili, gdy aplikacja go zdejmie.
 */
export interface EkranStartowyProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  uklad?: 'pelny' | 'w-tresci';
  /** Nazwa organu lub produktu bez „INSOLITUM", np. „Agenda". */
  nazwa?: ReactNode;
  /** Komunikat dla czytnika ekranu. */
  komunikat?: string;
  /** Poświata za znakiem — wyłącznie ekrany platformy (powłoka, Universe Mobile). */
  poswiata?: boolean;
  /** Zdanie pod znakiem: co się dzieje i jak długo (napis albo `<p>…</p>`). */
  tresc?: ReactNode;
  /** Akcje pod treścią — elementy `btn btn--primary|secondary btn--lg`. */
  akcje?: ReactNode;
  /** `praca` — system pracuje (domyślnie); `oddech` — system czeka na człowieka. */
  tryb?: 'praca' | 'oddech';
  /**
   * Czy coś jeszcze trwa (domyślnie tak). `false` — czekanie przerwała
   * awaria: bez wiersza „To trwa dłużej…”, który byłby wtedy nieprawdą.
   */
  trwa?: boolean;
  /** Szczegóły techniczne pod akcjami (kod, ścieżka) — jak w `EkranBledu`. */
  szczegoly?: SzczegolyBledu;
}

/**
 * Najdłuższa przerwa między zniknięciem jednego ekranu startu a pojawieniem
 * się następnego, przy której to nadal TEN SAM start. Strażnicy podmieniają
 * ekran w jednym zatwierdzeniu Reacta (przerwa 0) albo przez kilka klatek
 * pustego miejsca (przekierowanie, `null` między strażnikami). Dłuższa
 * przerwa to widok, który człowiek zobaczył — kolejny ekran startu jest
 * nowym czekaniem i ma swoje opóźnienia od początku (inaczej po minucie pracy
 * „To trwa dłużej niż zwykle…" stałoby od pierwszej klatki).
 */
export const OKNO_CIAGLOSCI_MS = 250;

/** Pamięć ekranu startu dla jednego układu — poza instancją. */
export interface PamiecStartu {
  /** Chwila pierwszego pokazania ekranu w tym ciągu (`performance.now()`). */
  start: number;
  /**
   * Od kiedy liczy się 8 s wiersza „To trwa dłużej…” — `start` albo
   * wcześniej, gdy ciąg zaczęła wstawka `index.html` (jej znak stoi, więc
   * praca sieci liczy się od `start`, a wiersz — od wstawki).
   */
  startDlugo?: number;
  /** Ile ekranów tego układu stoi teraz na ekranie. */
  zamontowane: number;
  /** Chwila ostatniego odmontowania; `-Infinity`, gdy jeszcze nie było. */
  odmontowano: number;
}

/**
 * Początek ciągu, do którego należy montaż w chwili `teraz`: początek
 * poprzedniego ekranu, jeśli ten stoi albo zniknął w oknie ciągłości;
 * w przeciwnym razie `teraz` (nowy start).
 */
export function startCiagu(pamiec: PamiecStartu | undefined, teraz: number): number {
  if (!pamiec) return teraz;
  const ciaglosc = pamiec.zamontowane > 0 || teraz - pamiec.odmontowano <= OKNO_CIAGLOSCI_MS;
  return ciaglosc ? Math.min(pamiec.start, teraz) : teraz;
}

const PAMIEC: Partial<Record<'pelny' | 'w-tresci', PamiecStartu>> = {};

const teraz = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Najwcześniejszy `startTime` animacji CSS, które JUŻ grają w poddrzewie
 * ekranu. Oś dokumentu ma ten sam początek co `performance.now()`, więc
 * wynik porównuje się wprost z chwilą montażu. Ekran narysowany przez React
 * w przeglądarce nie ma jeszcze takich animacji (czekają na klatkę,
 * `startTime` jest `null`) — wtedy, i w silnikach bez `getAnimations`
 * (starsze WebView), wynik to `Infinity` i zostaje chwila montażu.
 */
function poczatekAnimacji(el: Element | null): number {
  let poczatek = Number.POSITIVE_INFINITY;
  if (!el || typeof el.getAnimations !== 'function') return poczatek;
  for (const a of el.getAnimations({ subtree: true })) {
    if (typeof a.startTime === 'number' && a.startTime < poczatek) poczatek = a.startTime;
  }
  return poczatek;
}

/**
 * Początek wstawki startowej `index.html` powłoki — `startTime` animacji jej
 * wiersza „To trwa dłużej…”, która gra od pierwszego malowania. Czytany przy
 * PIERWSZYM renderze: `createRoot` zdejmuje wstawkę dopiero przy pierwszym
 * zatwierdzeniu, więc w fazie renderu jeszcze stoi. Bez wstawki (organy,
 * każdy kolejny montaż) — `Infinity`.
 */
function poczatekWstawki(): number {
  if (typeof document === 'undefined') return Number.POSITIVE_INFINITY;
  return poczatekAnimacji(document.querySelector('[data-ids-wstawka-startu]'));
}

/** Odnośnik wiersza „dłużej”: przeładowanie strony, jak we wstawce `index.html`. */
const ODSWIEZ = 'Odśwież stronę';

export function EkranStartowy({
  uklad = 'pelny',
  nazwa,
  komunikat = 'Wczytywanie Insolitum Universe…',
  poswiata = false,
  tresc,
  akcje,
  tryb = 'praca',
  trwa = true,
  szczegoly,
  className,
  style,
  ...props
}: EkranStartowyProps) {
  // Liczone przy PIERWSZYM renderze instancji, nie w efekcie: obraz z właściwym
  // opóźnieniem ma stać już w pierwszej klatce (efekt bierny przychodzi po
  // malowaniu — byłoby jedno mignięcie nazwy). Na serwerze i przy hydratacji
  // pamięć jest pusta, więc znacznik zgadza się z HTML-em z serwera.
  const [chwila] = useState(() => {
    const t = teraz();
    if (typeof window === 'undefined') return { start: t, dlugo: t, t };
    const pamiec = PAMIEC[uklad];
    const start = startCiagu(pamiec, t);
    // Dalszy ciąg startu liczy wiersz od początku ciągu; nowy start pełny —
    // od wstawki `index.html`, jeśli to ona zaczęła start strony.
    const dalszyCiag = Boolean(pamiec) && start < t;
    const dlugo = dalszyCiag
      ? Math.min(start, pamiec?.startDlugo ?? start)
      : Math.min(t, uklad === 'pelny' ? poczatekWstawki() : t);
    return { start, dlugo, t };
  });
  const uplynelo = Math.round(chwila.t - chwila.start);
  const uplyneloDlugo = Math.round(chwila.t - chwila.dlugo);
  const korzen = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Obraz mógł grać, zanim React go zobaczył (HTML z serwera). Dalszego
    // ciągu to nie przesuwa: jego początek jest starszy niż każda animacja
    // tego węzła.
    const start = Math.min(chwila.start, poczatekAnimacji(korzen.current));
    const p = PAMIEC[uklad] ?? {
      start,
      zamontowane: 0,
      odmontowano: Number.NEGATIVE_INFINITY,
    };
    PAMIEC[uklad] = p;
    p.start = start;
    p.startDlugo = Math.min(chwila.dlugo, start);
    p.zamontowane += 1;
    return () => {
      p.zamontowane -= 1;
      p.odmontowano = teraz();
    };
  }, [uklad, chwila]);

  const zTrescia = Boolean(tresc || akcje);
  // Ten sam zestaw i ta sama kolejność wierszy co w `EkranBledu`.
  const wpisySzczegolow = szczegoly
    ? (
        [
          ['Kod', szczegoly.kod],
          ['Identyfikator', szczegoly.identyfikator],
          ['Ścieżka', szczegoly.sciezka],
          ['Wersja', szczegoly.wersja],
        ] as const
      ).filter(([, v]) => Boolean(v))
    : [];

  return (
    <div
      ref={korzen}
      className={cn(
        'ids-ekran-startowy',
        `ids-ekran-startowy--${uklad}`,
        poswiata && 'ids-ekran-startowy--poswiata',
        zTrescia && 'ids-ekran-startowy--z-trescia',
        className
      )}
      role={zTrescia ? undefined : 'status'}
      style={
        uplynelo > 0 || uplyneloDlugo > uplynelo
          ? ({
              ...style,
              ...(uplynelo > 0 && { '--ids-start-uplynelo': `${uplynelo}ms` }),
              ...(uplyneloDlugo > uplynelo && {
                '--ids-start-uplynelo-dlugo': `${uplyneloDlugo}ms`,
              }),
            } as CSSProperties)
          : style
      }
      {...props}
    >
      <span className="ids-ekran-startowy__komunikat" role={zTrescia ? 'status' : undefined}>
        {komunikat}
      </span>
      {/* Znak stoi DOKŁADNIE na środku — nazwa i wiersz „dłużej” wiszą pod nim
          poza przepływem, więc pozycja znaku jest ta sama, co w `index.html`
          powłoki, i montaż Reacta nie przesuwa obrazu. Z treścią pod znakiem
          środek trzyma górny odstęp (`--z-trescia` w arkuszu), a nazwa
          i wiersz „dłużej” wchodzą do przepływu nad treścią. */}
      <div className="ids-ekran-startowy__srodek">
        {uklad === 'pelny' ? (
          <SygnetZywy uklad="pionowe" tryb={tryb} className="ids-ekran-startowy__znak" />
        ) : (
          <SygnetZywy tryb={tryb} className="ids-ekran-startowy__znak" />
        )}
        <div className="ids-ekran-startowy__pod">
          {/* `aria-hidden` PRZED klasą: bramki czytają znacznik `…__nazwa">Agenda<`. */}
          {nazwa ? (
            <p aria-hidden className="ids-znak-podpis ids-ekran-startowy__nazwa">
              {nazwa}
            </p>
          ) : null}
          {trwa ? (
            <p className="ids-ekran-startowy__dlugo">
              {akcje ? (
                'To trwa dłużej niż zwykle.'
              ) : (
                <>
                  To trwa dłużej niż zwykle. <a href="">{ODSWIEZ}</a>
                </>
              )}
            </p>
          ) : null}
        </div>
      </div>
      {tresc ? (
        <div className="ids-tresc-ekranu ids-ekran-startowy__tresc">
          {bezSierotekWTresci(tresc)}
        </div>
      ) : null}
      {akcje ? <div className="ids-ekran-startowy__akcje">{akcje}</div> : null}
      {wpisySzczegolow.length > 0 ? (
        <details className="ids-ekran-startowy__szczegoly">
          <summary>Szczegóły techniczne</summary>
          <dl>
            {wpisySzczegolow.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>
                  <code>{v}</code>
                </dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </div>
  );
}
